import { Link } from "react-router-dom";
import Seo, { breadcrumbs } from "../components/Seo";
import { SectionHeader } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
import { reviews } from "../data/reviews";
import { siteConfig } from "../config";

// Schema.org Review markup so Google can render ⭐ snippets in search results.
// We score everything 5/5 because these are curated positive testimonials (matching
// dealership policy: no star ratings shown ON-page; structured data only).
const REVIEW_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Scott Hall",
  jobTitle: "Vehicle Consultant",
  worksFor: {
    "@type": "AutoDealer",
    name: "Scott Sells Chevys",
    url: "https://scottsellschevys.com",
    areaServed: "Central Ohio",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    bestRating: "5",
    ratingCount: String(reviews.length),
    reviewCount: String(reviews.length),
  },
  review: reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    reviewBody: r.text,
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    itemReviewed: { "@type": "Person", name: "Scott Hall" },
  })),
};

const HIGHLIGHTS = [
  ["Knowledgeable Vehicle Help", "New, used, EVs — Scott digs into the details."],
  ["No-Pressure Shopping", "You set the pace. Scott helps you understand options."],
  ["Patient Communication", "Texts, calls, emails — Scott responds the way you prefer."],
  ["New and Used Vehicle Guidance", "Real comparisons, not generic talking points."],
];

const CtaBlock = () => (
  <div className="ssc-card-dark text-center">
    <h3 className="text-xl font-extrabold">Want help finding your next vehicle?</h3>
    <p className="text-[var(--ssc-silver)] mt-2 max-w-xl mx-auto text-sm">
      Tell Scott what you're looking for and get personal help with new vehicles, used vehicles, trade value, payment questions, EV options, or test drive timing.
    </p>
    <div className="mt-4 flex flex-wrap gap-2 justify-center">
      <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="rev-text">Text Scott</a>
      <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-ghost">Find Me a Vehicle</Link>
      <Link to="/new-vehicles" className="ssc-btn ssc-btn-ghost">Shop New</Link>
      <Link to="/used-vehicles" className="ssc-btn ssc-btn-ghost">Shop Used</Link>
      <Link to="/scotts-deal-desk" className="ssc-btn ssc-btn-ghost">Build My Own Deal</Link>
    </div>
  </div>
);

export default function Reviews() {
  const featured = reviews.slice(4, 7); // EV / no-pressure / patient
  return (
    <main id="main-content">
      <Seo
        title="Reviews | Scott Sells Chevys"
        description="Read customer feedback and learn how Scott Hall helps Central Ohio drivers find new and used vehicles with a helpful, knowledgeable, no-pressure approach."
        canonicalPath="/reviews"
        jsonLd={[breadcrumbs([{ name: "Home", path: "/" }, { name: "Reviews", path: "/reviews" }]), REVIEW_JSONLD]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Customer Reviews for Scott Hall</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">
            Real customer feedback about Scott's helpful, knowledgeable, and no-pressure approach to vehicle shopping.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-red">Find Me a Vehicle</Link>
            <Link to="/new-vehicles" className="ssc-btn ssc-btn-ghost">Shop New Vehicles</Link>
            <Link to="/used-vehicles" className="ssc-btn ssc-btn-ghost">Shop Used Vehicles</Link>
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary">Text Scott</a>
          </div>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          <AboutScottBlurb
            heading="The Kind of Experience Scott Aims For"
            text="These reviews reflect the kind of experience Scott aims to provide: helpful answers, patience, product knowledge, no-pressure guidance, and a personal approach built from over 15 years in the car business."
            testId="reviews-about-scott"
            className="mb-6"
          />
          <SectionHeader title="Featured Reviews" />
          <div className="grid md:grid-cols-3 gap-5">
            {featured.map((r, i) => (
              <blockquote key={i} className="ssc-card">
                <p className="text-[var(--ssc-ink-2)]">"{r.text}"</p>
                <footer className="mt-3 text-[var(--ssc-navy)] font-bold text-sm">— {r.name}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container">
          <SectionHeader title="All Customer Reviews" subtitle="Patience, product knowledge, and a no-pressure approach across new and used vehicles." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.map((r, i) => (
              <blockquote key={i} className="ssc-card" data-testid={`review-${i}`}>
                <p className="text-[var(--ssc-ink-2)] text-sm leading-relaxed">"{r.text}"</p>
                <footer className="mt-3 text-[var(--ssc-navy)] font-bold text-xs">— {r.name}</footer>
              </blockquote>
            )).reduce((acc, el, i) => {
              acc.push(el);
              if ((i + 1) % 6 === 0) acc.push(<div key={`cta-${i}`} className="md:col-span-2 lg:col-span-3"><CtaBlock /></div>);
              return acc;
            }, [])}
          </div>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader title="Why customers come back to Scott" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HIGHLIGHTS.map(([t, p]) => (
              <div key={t} className="ssc-card">
                <div className="font-extrabold text-[var(--ssc-navy)]">{t}</div>
                <p className="text-sm text-[var(--ssc-ink-2)] mt-1">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
